import {useEffect, useMemo, useState} from 'react';
import {flushEditorChanges} from '../../components/editors/useEditorFrame';
import {draftScope, draftWriteId, loadDraft, saveDraft} from './practiceDraftStorage';
import {readLegacyDraft} from './legacyPracticeDrafts';

function createSession(scope) {
    let records = {}, status = {}, timer, maxTimer;
    const loaded = new Set(), loading = new Map(), revisions = new Map();
    const pending = new Map(), writing = new Map(), listeners = new Set();
    const emit = () => listeners.forEach(listener => listener());
    const setStatus = (id, state, message) => {status = {...status, [id]:{state, message}}; emit();};
    const flush = () => {
        clearTimeout(timer); clearTimeout(maxTimer); maxTimer = null;
        pending.forEach((record, id) => {
            if (writing.has(id) || status[id]?.state === 'conflict') return;
            writing.set(id, record);
            setStatus(id, 'saving');
            saveDraft(scope, record, revisions.get(id) ?? null).then(revision => {
                revisions.set(id, revision); writing.delete(id);
                if (pending.get(id) === record) {pending.delete(id); setStatus(id, 'saved');}
                else flush(); // Serialize newer edits after the preceding generation.
            }).catch(error => {
                writing.delete(id);
                setStatus(id, error.code === 'functions/aborted' ? 'conflict' : 'error',
                    error.code === 'functions/resource-exhausted' ? error.message : '');
            });
        });
    };
    const session = {
        subscribe(listener) {listeners.add(listener); return () => listeners.delete(listener);},
        snapshot(id) {return {records, status, ready:!id || loaded.has(id), loadError:status[id]?.state === 'load-error'};},
        async load(question, force = false) {
            if (!scope || !question || pending.has(question.id) || writing.has(question.id)) return;
            const id = question.id;
            if (loading.has(id)) return loading.get(id);
            if (loaded.has(id) && !force) return;
            const task = (async () => {
                try {
                    const saved = await loadDraft(scope, id);
                    // An edit may have happened during a background refresh.
                    // Its old revision must survive so saving can detect conflict.
                    if (pending.has(id) || writing.has(id)) return;
                    let legacy;
                    if (!saved.record && !loaded.has(id)) legacy = await readLegacyDraft(scope, id);
                    if (!loaded.has(id) || revisions.get(id) !== saved.revision) {
                        const restored = saved.record ? {...saved.record} : undefined;
                        if (records[id] && restored) restored.epoch = Math.max(restored.epoch, records[id].epoch + 1);
                        records = {...records, [id]:restored};
                        revisions.set(id, saved.revision); loaded.add(id);
                        setStatus(id, saved.record ? 'saved' : 'idle');
                    }
                    if (legacy?.type === question.type && !pending.has(id) && !records[id]) {
                        session.update(question, {answer:legacy.answer, outcome:legacy.outcome, savedIn:legacy.savedIn || {}, epoch:legacy.epoch}, 0);
                        flush();
                    }
                } catch (error) {
                    // Never open a blank editor over a failed initial read.
                    if (!loaded.has(id)) setStatus(id, 'load-error');
                } finally {loading.delete(id); emit();}
            })();
            loading.set(id, task); return task;
        },
        update(question, patch, epoch = 0) {
            const id = question.id;
            if (!scope || !loaded.has(id)) return;
            const previous = records[id];
            if ((previous?.epoch || 0) !== epoch) return;
            const base = previous?.type === question.type ? previous : {epoch, savedIn:{}};
            const changes = typeof patch === 'function' ? patch(base) : patch;
            const record = {...base, ...changes, version:2, questionId:id, type:question.type, writeId:draftWriteId()};
            records = {...records, [id]:record}; pending.set(id, record);
            // Editing during an error/conflict must not conceal the warning.
            if (!['error', 'conflict'].includes(status[id]?.state)) setStatus(id, 'saving');
            clearTimeout(timer); timer = setTimeout(flush, 800);
            if (!maxTimer) maxTimer = setTimeout(flush, 5000);
            emit();
        },
        reset(question) {
            const epoch = records[question.id]?.epoch || 0;
            session.update(question, {answer:undefined, outcome:undefined, savedIn:{}, epoch:epoch + 1}, epoch);
            flush(); // A tombstone prevents old local answers being imported again.
        },
        async useCloud(question) {
            const id = question.id;
            if (writing.has(id)) return false;
            // Keep the unsaved copy if its replacement cannot be downloaded.
            try {
                const saved = await loadDraft(scope, id);
                pending.delete(id); revisions.set(id, saved.revision);
                const restored = saved.record || {version:2, questionId:id, type:question.type, savedIn:{}};
                records = {...records, [id]:{...restored, epoch:Math.max(restored.epoch || 0, (records[id]?.epoch || 0) + 1)}}; loaded.add(id);
                setStatus(id, saved.record ? 'saved' : 'idle');
                return true;
            } catch {setStatus(id, 'conflict', 'Could not load the cloud answer. Check your connection and retry.'); return false;}
        },
        flush,
        protect() {flushEditorChanges(); flush(); return pending.size === 0 && writing.size === 0;},
    };
    return session;
}

export default function usePracticeDrafts(userId, paperId, question) {
    const scope = userId && paperId ? draftScope(userId, paperId) : null;
    const session = useMemo(() => createSession(scope), [scope]);
    const [, render] = useState(0);
    useEffect(() => session.subscribe(() => render(value => value + 1)), [session]);
    useEffect(() => {session.load(question, true);}, [session, question]);
    useEffect(() => {
        const leave = event => {if (!session.protect()) {event.preventDefault(); event.returnValue = '';}};
        const hide = () => session.protect();
        const online = () => session.flush();
        window.addEventListener('beforeunload', leave); window.addEventListener('pagehide', hide); window.addEventListener('online', online);
        return () => {session.protect(); window.removeEventListener('beforeunload', leave); window.removeEventListener('pagehide', hide); window.removeEventListener('online', online);};
    }, [session]);
    useEffect(() => {
        const focus = () => session.load(question, true);
        const visibility = () => {if (document.visibilityState === 'hidden') session.protect(); else focus();};
        window.addEventListener('focus', focus); document.addEventListener('visibilitychange', visibility);
        return () => {window.removeEventListener('focus', focus); document.removeEventListener('visibilitychange', visibility);};
    }, [session, question]);
    return {...session.snapshot(question?.id), update:session.update, reset:session.reset, retry:session.flush,
        reload:() => session.load(question, true), useCloud:() => session.useCloud(question),
        protect:session.protect, flush:() => {flushEditorChanges(); session.flush();}};
}
